import {
	Component,
	OnInit,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	ViewChild,
	ElementRef,
	Inject,
	Renderer2,
	OnDestroy,
	OnChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { DateTimeAdapter, OWL_DATE_TIME_FORMATS } from 'ng-pick-datetime';
import { IdService, FormControlBase } from '../../shared/index';
import { IDatepickerDisabledDates } from './datepicker.types';

import * as _moment from 'moment';

const moment = (_moment as any).default ? (_moment as any).default : _moment;

export const CUSTOM_FORMATS = {
	parseInput: 'D MMMM YYYY',
	fullPickerInput: 'D MMMM YYYY hh:mm a',
	datePickerInput: 'D MMMM YYYY',
	timePickerInput: 'hh:mm a',
	monthYearLabel: 'MMM YYYY',
	dateA11yLabel: 'LL',
	monthYearA11yLabel: 'MMMM-YYYY'
};

@Component({
	selector: 'rbc-datepicker',
	templateUrl: './datepicker.component.html',
	styleUrls: ['./datepicker.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DatepickerComponent),
			multi: true
		},
		{
			provide: OWL_DATE_TIME_FORMATS,
			useValue: CUSTOM_FORMATS
		}
	]
})
export class DatepickerComponent extends FormControlBase<string> implements OnInit, OnDestroy, OnChanges {
	set isDateValid(value: boolean) {
		this._isDateValid = value;
		if (!this.overlayRef) {
			return;
		}

		if (value) {
			this.renderer.removeClass(this.overlayRef, 'rbc-datepicker__panel');
		} else {
			this.renderer.addClass(this.overlayRef, 'rbc-datepicker__panel');
		}
	}

	get isDateValid() {
		return this._isDateValid;
	}

	@Input() set value(value) {
		if (!value) {
			return;
		}
		this.writeValue(typeof value === 'string' ? new moment(value, this.owlDateTimeFormat.parseInput) : value);
	}
	@Input() set format(format: string) {
		if (format) {
			this.owlDateTimeFormat.datePickerInput = format;
			this.owlDateTimeFormat.parseInput = format;
		}
	}
	@Input() set openDate(openDate: Date | string) {
		this._openDate = typeof openDate === 'string' ? new Date(openDate) : openDate;
	}
	get openDate() {
		return this._openDate;
	}
	@Input() set disabledDates(disabledDates: IDatepickerDisabledDates) {
		if (!disabledDates) {
			return;
		}

		if (disabledDates.from) {
			this.dateMaxValue = disabledDates.from;
		}

		if (disabledDates.to) {
			this.dateMinValue = disabledDates.to;
		}
	}
	@Input() set language(language: string) {
		if (language && language === 'fr') {
			this.dateTimeAdapter.setLocale(language);
		}
	}
	_value;
	overlayRef;
	_isDateValid: boolean = true;
	dateMinValue;
	dateMaxValue;

	@Input() name: string;
	@Input() errorLabel: string;
	@Input() helpText: string;
	@Input() label: string;
	@Input() ariaLabelledby: string;
	@Input() id: string;
	@Input() placeholder: string = '';
	@Input() required: boolean = false;
	@Input() inline: boolean = false;
	@Input() isDisabled: boolean = false;
	@Input() mondayFirst: boolean = false;
	@Input() typeable: boolean = true;
	_openDate: Date | string;

	@Output() opened = new EventEmitter();
	@Output() closed = new EventEmitter();
	@Output() selected = new EventEmitter();
	@Output() onInput = new EventEmitter();
	@Output() cleared = new EventEmitter();
	@Output() changedMonth = new EventEmitter();
	@Output() changedYear = new EventEmitter();

	@ViewChild('dateInputElm') dateInputElm: ElementRef;

	constructor(
		@Inject(DOCUMENT) private document,
		@Inject(OWL_DATE_TIME_FORMATS) private owlDateTimeFormat,
		private dateTimeAdapter: DateTimeAdapter<any>,
		private renderer: Renderer2,
		private idService: IdService
	) {
		super();
	}

	ngOnInit() {
		if (!this.id) {
			this.id = this.idService.generate();
		}
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	pickerOpened() {
		if (!this.overlayRef) {
			this.overlayRef = this.document.getElementById('cdk-overlay-0');
		}
		this.opened.emit();
		this.dateInputElm.nativeElement.focus();
	}

	pickerClosed() {
		this.closed.emit();
		this.touch();
	}

	valueChanged(newValue) {
		this.isDateValid = true;
		this.setModel(newValue);
		this.selected.emit(newValue);
	}

	onInputBlur(e) {
		let val = e.target.value;
		const newVal = new moment(val, this.owlDateTimeFormat.parseInput);

		if (!val) {
			this.cleared.emit();
			return;
		}

		if (newVal.isValid()) {
			this.value = newVal;
			this.isDateValid = true;
		} else {
			this.dateInputElm.nativeElement.value = '';
			this.isDateValid = false;
			val = '';
			this.setModel('');
			this.cleared.emit();
		}
		this.onInput.emit(val);
		this.touch();
	}

	onYearChange(e) {
		this.changedYear.emit(e);
	}

	onMonthChange(e) {
		this.changedMonth.emit(e);
	}
}
